import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  UserCheck, UserX, Search, Loader2, ArrowLeft, ArrowRight,
  CalendarDays, Clock, MapPin, Users, Download, Ticket,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

export default function GlobalEventAttendanceModal({ open, onClose, event }) {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [passportFilter, setPassportFilter] = useState(""); // "" = all, "_none" = no passport, else passport_id
  const [busyId, setBusyId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const refresh = useCallback(async (opts = {}) => {
    if (!event) return;
    if (!opts.silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/events/${event.id}/attendees`, { withCredentials: true });
      setAttendees(Array.isArray(data) ? data : []);
    } catch {
      if (!opts.silent) toast.error("Failed to load attendance");
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, [event]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  // Live polling while open so kiosk check-ins appear in real time.
  useEffect(() => {
    if (!open) return undefined;
    const handle = setInterval(() => { refresh({ silent: true }); }, 4000);
    return () => clearInterval(handle);
  }, [open, refresh]);

  const q = search.trim().toLowerCase();
  const matchQ = (a) =>
    !q
    || `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase().includes(q)
    || (a.email || "").toLowerCase().includes(q)
    || (a.passport_name || "").toLowerCase().includes(q);

  const matchPassport = (a) => {
    if (!passportFilter) return true;
    if (passportFilter === "_none") return !a.passport_id;
    return a.passport_id === passportFilter;
  };

  // Distinct passports surfaced from the current attendee list — sorted for
  // a stable dropdown. Includes "_none" pseudo-option when any unassigned
  // attendees exist.
  const passportOptions = (() => {
    const seen = new Map();
    let hasNone = false;
    for (const a of attendees) {
      if (a.passport_id) {
        if (!seen.has(a.passport_id)) seen.set(a.passport_id, a.passport_name || "(unnamed)");
      } else {
        hasNone = true;
      }
    }
    const out = Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((x, y) => x.name.localeCompare(y.name));
    if (hasNone) out.push({ id: "_none", name: "No Passport" });
    return out;
  })();

  const checkedInList = attendees
    .filter((a) => !!a.checked_in_at)
    .filter(matchPassport)
    .map((a) => ({
      id: a.id,
      first_name: a.first_name,
      last_name: a.last_name,
      email: a.email,
      timestamp: a.checked_in_at,
      passport_name: a.passport_name,
    }))
    .filter(matchQ)
    // Most-recently checked in at the top, descending by timestamp
    .sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));

  const notCheckedIn = attendees
    .filter((a) => !a.checked_in_at)
    .filter(matchPassport)
    .filter(matchQ);

  const checkIn = async (attendeeId) => {
    setBusyId(attendeeId);
    try {
      await axios.post(`${API}/events/${event.id}/attendees/${attendeeId}/checkin`, {}, { withCredentials: true });
      toast.success("Marked as checked in");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to check in");
    } finally { setBusyId(null); }
  };

  const undoCheckIn = async (attendeeId) => {
    setBusyId(attendeeId);
    try {
      await axios.delete(`${API}/events/${event.id}/attendees/${attendeeId}/checkin`, { withCredentials: true });
      toast.success("Removed from checked-in list");
      await refresh();
    } catch {
      toast.error("Failed to undo");
    } finally { setBusyId(null); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = [["First Name", "Last Name", "Email", "Passport", "Checked In At"]];
      for (const a of attendees) {
        rows.push([
          a.first_name || "",
          a.last_name || "",
          a.email || "",
          a.passport_name || "",
          a.checked_in_at || "",
        ]);
      }
      const csv = rows.map((r) => r.map((c) => {
        const s = String(c);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (event.name || "event").replace(/[^A-Za-z0-9_-]+/g, "_");
      a.href = url;
      a.download = `${safe}_attendance.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export CSV");
    } finally { setExporting(false); }
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] p-0 flex flex-col overflow-hidden bg-white border-gray-200"
        data-testid="global-event-attendance-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-gray-200">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <DialogTitle className="text-gray-900 font-heading text-xl font-bold">
                Attendance — {event.name}
              </DialogTitle>
              <DialogDescription className="text-gray-600 font-body text-xs flex flex-wrap gap-x-4 gap-y-1 mt-1">
                <span className="flex items-center gap-1.5"><CalendarDays className="h-3 w-3" />{event.date}</span>
                {event.start_time && <span className="flex items-center gap-1.5"><Clock className="h-3 w-3" />{event.start_time}</span>}
                {event.location && <span className="flex items-center gap-1.5"><MapPin className="h-3 w-3" />{event.location}</span>}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleExport}
                disabled={exporting || attendees.length === 0}
                variant="outline"
                size="sm"
                className="gap-1.5 border-gray-300 text-gray-700 hover:bg-gray-100"
                data-testid="attendance-export-csv"
              >
                {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Export CSV
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 py-3 shrink-0 border-b border-gray-200 bg-gray-50/50">
          <div className="flex gap-2 items-center flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by name, email, or passport…"
                data-testid="attendance-search"
                className="pl-10 h-9 bg-white border-gray-300 text-gray-900 text-sm placeholder:text-gray-400"
              />
            </div>
            {passportOptions.length > 0 ? (
              <select
                value={passportFilter}
                onChange={(e) => setPassportFilter(e.target.value)}
                data-testid="attendance-passport-filter"
                className="h-9 rounded-md border border-gray-300 bg-white text-gray-900 text-sm px-3 focus:outline-none focus:border-gray-500"
              >
                <option value="">All passports</option>
                {passportOptions.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            ) : null}
            {(passportFilter || search) ? (
              <button
                onClick={() => { setPassportFilter(""); setSearch(""); }}
                className="text-xs font-body text-gray-500 underline hover:text-gray-700"
                data-testid="attendance-clear-filters"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50/30">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <Column
                title="Checked In"
                icon={UserCheck}
                iconColor="#10b981"
                count={checkedInList.length}
                testid="attendance-checked-in"
              >
                {checkedInList.length === 0 ? (
                  <EmptyHint text={search ? "No matches in this list." : "No one has checked in yet."} />
                ) : (
                  checkedInList.map((c) => (
                    <Row
                      key={`in-${c.id}`}
                      testid={`attendance-in-${c.id}`}
                      first={c.first_name}
                      last={c.last_name}
                      passport={c.passport_name}
                      meta={<><Clock className="h-3 w-3" /> {fmtTime(c.timestamp)}</>}
                      actionIcon={ArrowRight}
                      actionLabel="Move to not checked in"
                      actionColor="#ef4444"
                      onAction={() => undoCheckIn(c.id)}
                      busy={busyId === c.id}
                    />
                  ))
                )}
              </Column>

              <Column
                title="Not Checked In"
                icon={UserX}
                iconColor="#6b7280"
                count={notCheckedIn.length}
                testid="attendance-not-checked-in"
              >
                {notCheckedIn.length === 0 ? (
                  <EmptyHint text={search ? "No matches in this list." : "Everyone in the roster has checked in."} />
                ) : (
                  notCheckedIn.map((c) => (
                    <Row
                      key={`out-${c.id}`}
                      testid={`attendance-out-${c.id}`}
                      first={c.first_name}
                      last={c.last_name}
                      meta={c.email ? <>{c.email}</> : null}
                      actionIcon={ArrowLeft}
                      actionLabel="Move to checked in"
                      actionColor="#10b981"
                      onAction={() => checkIn(c.id)}
                      busy={busyId === c.id}
                      reverse
                    />
                  ))
                )}
              </Column>
            </div>
          )}
        </div>

        <div className="px-6 py-3 shrink-0 border-t border-gray-200 bg-white flex justify-between items-center">
          <span className="text-xs font-body text-gray-600 flex items-center gap-1.5" data-testid="attendance-footer-count">
            <Users className="h-3 w-3" />
            {passportFilter
              ? `${checkedInList.length} / ${checkedInList.length + notCheckedIn.length} checked in (filtered)`
              : `${attendees.filter((a) => a.checked_in_at).length} / ${attendees.length} checked in`}
          </span>
          <Button variant="ghost" onClick={onClose} className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 text-sm" data-testid="attendance-close">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Column({ title, icon: Icon, iconColor, count, total, testid, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/70">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" style={{ color: iconColor }} />
          <h4 className="font-heading text-sm font-bold text-gray-900">{title}</h4>
        </div>
        <Badge className="bg-gray-100 text-gray-700 border-gray-200 text-xs hover:bg-gray-100">
          {count}{typeof total === "number" && total !== count ? ` / ${total}` : ""}
        </Badge>
      </div>
      <div className="divide-y divide-gray-100 max-h-[55vh] overflow-y-auto">{children}</div>
    </div>
  );
}

function Row({ testid, first, last, passport, removed, meta, actionIcon: Icon, actionLabel, actionColor, onAction, busy }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`text-sm font-body truncate ${removed ? "italic text-gray-500" : "text-gray-900"}`}>{first} {last}</p>
          {removed ? (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[9px] uppercase tracking-wider hover:bg-amber-50">
              Removed
            </Badge>
          ) : null}
          {passport ? (
            <Badge className="bg-blue-50 text-blue-700 border-blue-100 text-[9px] uppercase tracking-wider hover:bg-blue-50 inline-flex items-center gap-0.5">
              <Ticket className="h-2.5 w-2.5" /> {passport}
            </Badge>
          ) : null}
        </div>
        {meta && (
          <p className="text-[11px] font-body text-gray-500 flex items-center gap-1 mt-0.5 truncate">{meta}</p>
        )}
      </div>
      <button
        onClick={onAction}
        disabled={busy}
        title={actionLabel}
        className="shrink-0 p-2 rounded hover:bg-gray-100 disabled:opacity-50 transition-colors"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : <Icon className="h-4 w-4" style={{ color: actionColor }} />}
      </button>
    </div>
  );
}

function EmptyHint({ text }) {
  return <p className="px-4 py-6 text-xs font-body text-gray-500 text-center">{text}</p>;
}
